/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useParkingLotForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Parking Lot form state, validation, and submission logic.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createParkingLot, updateParkingLot } from '../api/parkingLotsApi';
import { createDefaultFormData } from '../utils/formDefaults';
import { generateContextFromName } from '../utils/contextHelpers';
import type { ParkingLotFormState, ValidationErrors } from '../types';

interface UseParkingLotFormOptions {
  formData: ParkingLotFormState;
  setFormData: React.Dispatch<React.SetStateAction<ParkingLotFormState>>;
  originalFormData: ParkingLotFormState;
  isEditMode: boolean;
  parkingLotId?: string;
  onLoadParkingLot?: (id: string) => Promise<void>;
}

export function useParkingLotForm({
  formData,
  setFormData,
  originalFormData,
  isEditMode,
  parkingLotId,
  onLoadParkingLot
}: UseParkingLotFormOptions) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('parkingLots.fields.name.required');
    }

    if (!formData.extension.trim()) {
      newErrors.extension = t('parkingLots.fields.extension.required');
    }

    if (formData.totalPositions < 1 || formData.totalPositions > 99) {
      newErrors.totalPositions = t('parkingLots.fields.totalPositions.min');
    }

    if (formData.parkingTime < 5 || formData.parkingTime > 300) {
      newErrors.parkingTime = t('parkingLots.fields.parkingTime.min');
    }

    if (formData.comebackTime < 5 || formData.comebackTime > 300) {
      newErrors.comebackTime = t('parkingLots.fields.comebackTime.min');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, t]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('parkingLots.messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        extension: formData.extension.trim(),
        totalPositions: formData.totalPositions,
        parkingTime: formData.parkingTime,
        comebackTime: formData.comebackTime,
        findSlot: formData.findSlot,
        courtesyTone: formData.courtesyTone,
        mohClass: formData.mohClass,
        returnToOriginator: formData.returnToOriginator,
        announcePosition: formData.announcePosition,
        callTransfer: formData.callTransfer,
        callReparking: formData.callReparking,
        callHangup: formData.callHangup,
        record: formData.record,
        announcementPath: formData.announcementPath || null,
        timeoutModule: formData.timeoutModule || null,
        timeoutDestination: formData.timeoutDestination || null,
        settings: formData.settings,
        enabled: formData.enabled
      };

      let savedData;
      if (isEditMode && parkingLotId) {
        savedData = await updateParkingLot(parkingLotId, payload);
        showSuccess(t('parkingLots.messages.saveSuccess'));
        if (onLoadParkingLot && savedData.id) {
          await onLoadParkingLot(savedData.id.toString());
        }
      } else {
        savedData = await createParkingLot(payload);
        showSuccess(t('parkingLots.messages.saveSuccess'));
        // Note: Navigation after create is now handled by the dropdown actions
        // in ParkingLotsFormView, so we don't navigate here automatically
      }

      return savedData;
    } catch (error) {
      console.error('Error saving parking lot:', error);
      showError(error.message || t('parkingLots.messages.saveError'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    validateForm,
    isEditMode,
    parkingLotId,
    hasChanges,
    showSuccess,
    showError,
    showInfo,
    t,
    onLoadParkingLot
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData({ ...originalFormData });
      setErrors({});
    } else {
      const defaults = createDefaultFormData();
      setFormData(defaults);
      setErrors({});
    }
  }, [isEditMode, originalFormData, setFormData]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel
  };
}

